import React, { useEffect, useMemo, useState } from 'react'

const norm = columns =>
  (Array.isArray(columns) ? columns : []).map((c, i) =>
    typeof c === 'string'
      ? { label: c, field: c, sort: 'asc' }
      : { label: c.label ?? c.field ?? '', field: c.field ?? c.label ?? `col${i}`, sort: c.sort ?? 'asc' }
  )

const asText = v => {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string' || typeof v === 'number') return `${v}`
  if (React.isValidElement(v)) {
    const kids = v.props && v.props.children
    if (typeof kids === 'string' || typeof kids === 'number') return `${kids}`
    return ''
  }
  return ''
}

const MDB4Datatable = ({
  data,
  className = '',
  striped = false,
  bordered = false,
  small = false,
  sm = false,
  hover = false,
  responsive = false,
  searching = true,
  paging = true,
  info = true,
  displayEntries = true,
  entries = 10,
  entriesOptions = [10, 20, 50, 100],
  sortRows = [],
  order,
  noRecordsFoundLabel = 'No matching records found',
  searchLabel = 'Search',
  entriesLabel = 'Show entries',
  infoLabel = ['Showing', 'to', 'of', 'entries'],
  paginationLabel = ['Previous', 'Next'],
  pagesAmount = 8,
  noBottomColumns = false,
  exportToCSV = false,
  onSearch,
}) => {
  const columns = useMemo(() => norm(data && data.columns), [data])
  const rows = useMemo(() => (data && Array.isArray(data.rows) ? data.rows : []), [data])

  const [perPage, setPerPage] = useState(entries)
  const [page, setPage] = useState(0)
  const [query, setQuery] = useState('')
  // MDB4's `order={[field, dir]}` sets the initial sort. The field is a column
  // `field` key, not a label (e.g. the Agent column's field is 'id').
  const [sort, setSort] = useState(() =>
    Array.isArray(order) && order.length
      ? { field: order[0], dir: `${order[1] || 'asc'}`.toLowerCase() === 'desc' ? 'desc' : 'asc' }
      : { field: null, dir: null }
  )

  useEffect(() => setPage(0), [query, perPage, rows.length])

  const filtered = useMemo(() => {
    const q = `${query}`.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(r => columns.some(c => asText(r[c.field]).toLowerCase().includes(q)))
  }, [rows, columns, query])

  const sorted = useMemo(() => {
    if (!sort.field || !sort.dir) return filtered
    const out = [...filtered]
    out.sort((a, b) => {
      const av = asText(a[sort.field]).toLowerCase()
      const bv = asText(b[sort.field]).toLowerCase()
      if (av === bv) return 0
      return (av < bv ? -1 : 1) * (sort.dir === 'asc' ? 1 : -1)
    })
    return out
  }, [filtered, sort])

  const total = sorted.length
  const totalPages = paging ? Math.max(1, Math.ceil(total / perPage)) : 1
  const current = Math.min(page, totalPages - 1)
  const view = paging ? sorted.slice(current * perPage, (current + 1) * perPage) : sorted

  const toggleSort = field => {
    if (Array.isArray(sortRows) && sortRows.length && !sortRows.includes(field)) return
    setSort(s => (s.field !== field ? { field, dir: 'asc' } : { field, dir: s.dir === 'asc' ? 'desc' : 'asc' }))
  }
  const thClass = field => (sort.field === field ? (sort.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting')

  const pages = useMemo(() => {
    const group = Math.floor(current / pagesAmount)
    return Array.from({ length: totalPages }, (_, i) => i).slice(group * pagesAmount, (group + 1) * pagesAmount)
  }, [totalPages, current, pagesAmount])

  const tableClass = [
    'table',
    bordered ? 'table-bordered' : '',
    small || sm ? 'table-sm' : '',
    striped ? 'table-striped' : '',
    hover ? 'table-hover' : '',
    'dataTable',
  ]
    .filter(Boolean)
    .join(' ')

  const csvHref = useMemo(
    () =>
      encodeURI(
        'data:text/csv;charset=utf-8,' +
          [columns.map(c => c.field).join(','), sorted.map(r => Object.values(r).join(',')).join('\n')].join('\n')
      ),
    [columns, sorted]
  )

  const infoText = !total
    ? `${infoLabel[0]} 0 ${infoLabel[3]}`
    : `${infoLabel[0]} ${current * perPage + 1} ${infoLabel[1]} ${Math.min((current + 1) * perPage, total)} ${
        infoLabel[2]
      } ${total} ${infoLabel[3]}`

  const head = (testId, cells) => (
    <thead data-test={testId}>
      <tr>{cells}</tr>
    </thead>
  )

  return (
    <div data-test='datatable' className={['dataTables_wrapper', 'dt-bootstrap4', className].filter(Boolean).join(' ')}>
      {(displayEntries || searching) && (
        <div className='row'>
          <div
            data-test='datatable-entries'
            className='col-sm-12 col-md-6'
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {displayEntries && (
              <div data-test='datatable-select' className='dataTables_length bs-select'>
                <label>
                  {entriesLabel}
                  <select
                    className='custom-select custom-select-sm form-control form-control-sm'
                    style={{ marginLeft: '0.5rem' }}
                    value={perPage}
                    onChange={e => setPerPage(parseInt(e.target.value, 10))}
                  >
                    {entriesOptions.map(o => (
                      <option key={`entries-${o}`} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
          <div
            data-test='datatable-search'
            className='col-sm-12 col-md-6'
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
          >
            {searching && (
              <div data-test='datatable-input' className='mdb-datatable-filter flex-row'>
                <input
                  className='form-control form-control-sm ml-0 my-1'
                  type='text'
                  placeholder={searchLabel}
                  aria-label={searchLabel}
                  value={query}
                  onChange={e => {
                    setQuery(e.target.value)
                    if (typeof onSearch === 'function') onSearch(e.target.value)
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className='row'>
        <div data-test='datatable-table' className='col-sm-12'>
          <div data-test='table' className={responsive ? 'table-responsive' : ''}>
            <table className={tableClass}>
              {head(
                'datatable-head',
                columns.map(c => (
                  <th key={`h-${c.field}`} className={thClass(c.field)} onClick={() => toggleSort(c.field)}>
                    {c.label}
                  </th>
                ))
              )}
              <tbody data-test='table-body'>
                {view.length ? (
                  view.map((r, i) => (
                    <tr
                      key={`r-${i}`}
                      onClick={Object.prototype.hasOwnProperty.call(r, 'clickEvent') ? r.clickEvent : undefined}
                    >
                      {columns.map(c => (
                        <td key={`c-${i}-${c.field}`}>{r[c.field]}</td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length}>{noRecordsFoundLabel}</td>
                  </tr>
                )}
              </tbody>
              {!noBottomColumns &&
                head(
                  'table-foot',
                  columns.map(c => <th key={`f-${c.field}`}>{c.label}</th>)
                )}
            </table>
          </div>
        </div>
      </div>

      {(info || paging) && (
        <div className='row'>
          <div data-test='datatable-info' className='col-sm-12 col-md-5'>
            {info && (
              <div className='dataTables_info' role='status' aria-live='polite'>
                {infoText}
              </div>
            )}
          </div>
          <div data-test='datatable-pagination' className='col-sm-12 col-md-7'>
            {paging && (
              <div className='dataTables_paginate'>
                <ul data-test='pagination' className='pagination'>
                  <li
                    data-test='page-item'
                    className={(current <= 0 ? 'disabled ' : '') + 'page-item'}
                    onClick={() => current > 0 && setPage(current - 1)}
                  >
                    <a data-test='page-link' aria-label={paginationLabel[0]} className='page-link page-link'>
                      <span>{paginationLabel[0]}</span>
                    </a>
                  </li>
                  {pages.map((p, i) => (
                    <li
                      data-test='page-item'
                      key={`p-${p}-${i}`}
                      className={(p === current ? 'active ' : '') + 'page-item'}
                      onClick={() => setPage(p)}
                    >
                      <a data-test='page-link' className='page-link page-link'>
                        {p + 1}
                        {p === current && <span className='sr-only'>(current)</span>}
                      </a>
                    </li>
                  ))}
                  <li
                    data-test='page-item'
                    className={(current >= totalPages - 1 ? 'disabled ' : '') + 'page-item'}
                    onClick={() => current < totalPages - 1 && setPage(current + 1)}
                  >
                    <a data-test='page-link' aria-label={paginationLabel[1]} className='page-link page-link'>
                      <span>{paginationLabel[1]}</span>
                    </a>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {exportToCSV && (
        <div className='row justify-content-between'>
          <div className='pl-3'></div>
          <div className='mr-2'>
            <a
              data-test='export-to-csv'
              role='button'
              type='link'
              className='btn btn-primary Ripple-parent'
              href={csvHref}
              download='export.csv'
            >
              Download CSV
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

export default MDB4Datatable
